import {
  TemplateResponseData,
  templatePlaceholderDetails,
  templateResponsePlaceholders,
  isValidFormRedirectUrl,
} from "@smart/bridge-templates-basic";
import {
  Alert,
  Editor,
  FieldList,
  Icon,
  IconName,
  Link,
  RadioButtons,
  richMessageFeatures,
  TextFieldInput,
} from "@smart/itops-sb-design-system-dom";

export type ConfirmationMode = "message" | "redirect";

const confirmationModeRadioClassName =
  "w-full gap-medium [&>label]:flex-1 [&>label]:items-start [&>label]:border [&>label]:border-neutral-200 [&>label:has(:checked)]:border-blue-560 [&>label:has(:checked)]:bg-blue-60 [&>label:has(:checked)]:hover:bg-blue-60 [&>label:has(:checked)]:focus-within:bg-blue-60";

const ConfirmationModeOptionLabel = ({
  icon,
  title,
  description,
}: {
  icon: IconName;
  title: string;
  description: string;
}) => (
  <div className="flex min-w-0 flex-1 flex-col gap-xSmall mt-[0.25rem] ml-[0.5rem]">
    <div className="flex items-center gap-small">
      <Icon name={icon} className="shrink-0 text-neutral-600" />
      <span className="text-ui font-semibold text-neutral-850">{title}</span>
    </div>
    <p className="text-caption text-neutral-500">{description}</p>
  </div>
);

export const ConfirmationModeToggle = ({
  mode,
  onModeChange,
}: {
  mode: ConfirmationMode;
  onModeChange: (mode: ConfirmationMode) => void;
}) => (
  <RadioButtons
    dataTestId="confirmation-mode-radio"
    box
    direction="row"
    withLabelWrapper={false}
    className={confirmationModeRadioClassName}
    value={mode}
    onChange={(value) => onModeChange(value as ConfirmationMode)}
    options={[
      {
        value: "message",
        label: "Show a message",
        labelOverride: (
          <ConfirmationModeOptionLabel
            icon="regularMessageLines"
            title="Show a message"
            description="Display a thank-you or confirmation message on the screen."
          />
        ),
      },
      {
        value: "redirect",
        label: "Redirect to a URL",
        labelOverride: (
          <ConfirmationModeOptionLabel
            icon="regularArrowsRotate"
            title="Redirect to a URL"
            description="Send the client to a page on your own website instead."
          />
        ),
      },
    ]}
  />
);

export const RedirectUrlField = ({
  redirectUrl,
  onRedirectUrlChange,
}: {
  redirectUrl: string;
  onRedirectUrlChange: (value: string) => void;
}) => {
  const trimmedRedirectUrl = redirectUrl.trim();
  const redirectUrlError =
    trimmedRedirectUrl && !isValidFormRedirectUrl(trimmedRedirectUrl)
      ? "Enter a valid http or https URL"
      : undefined;
  const isRedirectUrlValid =
    !!trimmedRedirectUrl && isValidFormRedirectUrl(trimmedRedirectUrl);

  return (
    <div className="flex flex-col gap-medium">
      <div className="flex flex-col gap-small">
        <FieldList>
          <TextFieldInput
            id="confirmation-redirect-url"
            dataTestId="confirmation-redirect-url"
            label="Redirect URL"
            mandatory
            leftIcon="regularGlobe"
            value={redirectUrl}
            onChange={(event) => onRedirectUrlChange(event.currentTarget.value)}
            error={!!redirectUrlError}
            message={redirectUrlError}
            className="[&_button_svg]:text-green-600"
            rightButtonIcon={
              isRedirectUrlValid ? "solidCircleCheck" : undefined
            }
          />
        </FieldList>
        <div className="flex items-start justify-between gap-medium">
          <p className="text-caption text-neutral-500">
            Enter the full address of the page on your website, including{" "}
            <span className="font-semibold">https://</span>.
          </p>
          {isRedirectUrlValid && (
            <Link
              text="Test link"
              href={trimmedRedirectUrl}
              rightIcon="solidUpRightFromSquare"
              target="_blank"
              dataTestId="confirmation-redirect-test-link"
              className="shrink-0"
            />
          )}
        </div>
      </div>
      <Alert
        variant="info"
        palette="branding"
        content={
          <p className="text-ui text-neutral-850">
            On a successful submission the client is sent straight to this page
            — the on-screen confirmation message (and its “save a copy of your
            form” link) won’t be shown. Make sure your destination page confirms
            that the form was received.
          </p>
        }
      />
    </div>
  );
};

export type ConfirmationSectionProps = {
  mode: ConfirmationMode;
  onModeChange: (mode: ConfirmationMode) => void;
  currentConfirmationContent: string;
  editedConfirmationMessage: string | undefined;
  onConfirmationChange: (value: string) => void;
  editorKey: number;
  responsePlaceholderData: Partial<TemplateResponseData>;
  redirectUrl: string;
  onRedirectUrlChange: (value: string) => void;
};

export const ConfirmationSection = ({
  mode,
  onModeChange,
  currentConfirmationContent,
  editedConfirmationMessage,
  onConfirmationChange,
  editorKey,
  responsePlaceholderData,
  redirectUrl,
  onRedirectUrlChange,
}: ConfirmationSectionProps) => (
  <div className="flex p-[2.4rem] pt-[0.8rem] flex-col gap-medium">
    <p className="text-ui text-neutral-500">
      Choose what your client or prospect sees once they successfully submit
      this form.
    </p>
    <ConfirmationModeToggle mode={mode} onModeChange={onModeChange} />

    {mode === "message" ? (
      <div className="flex max-h-[32.15rem] flex-col">
        <Editor
          key={editorKey}
          id="confirmation-message"
          dataTestId="confirmation-message-editor"
          initialTextValue={
            editedConfirmationMessage ?? currentConfirmationContent
          }
          onValueChange={onConfirmationChange}
          placeholders={{
            keys: templateResponsePlaceholders,
            details: templatePlaceholderDetails,
            data: responsePlaceholderData,
          }}
          featureOptions={richMessageFeatures}
          mandatory
        />
      </div>
    ) : (
      <RedirectUrlField
        redirectUrl={redirectUrl}
        onRedirectUrlChange={onRedirectUrlChange}
      />
    )}
  </div>
);
